import { PropsWithChildren, useMemo } from 'react';
import { useFieldArray } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sendTestMessage } from 'api/engage';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentUser } from 'customHooks/useCurrentUser';
import { useForceRender } from 'customHooks/useForceRender';
import { EngageMessageChannel } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface TestMessageFormValues {
    entries: { value: string }[];
}

interface SendTestMessageDialogProps {
    channel: EngageMessageChannel;
    messageId: number;
}

const SendTestMessageDialog = ({
    children,
    channel,
    messageId,
}: PropsWithChildren<SendTestMessageDialogProps>) => {
    const { key, forceRender } = useForceRender();
    const { accountId } = useCurrentAccount();
    const { email, phone } = useCurrentUser();
    const { success, error } = useToast();
    const defaultValues = useMemo(() => {
        let entries = [];
        if (channel === 'email') {
            entries.push(email ? { value: email } : { value: '' });
        }
        if (channel === 'sms') {
            entries.push(phone ? { value: phone } : { value: '' });
        }
        return { entries };
    }, [channel, email, phone]);

    const methods = Form.useGivebutterForm<TestMessageFormValues>({
        defaultValues,
    });

    const { control, path, handleSubmit } = methods;

    const {
        fields: entries,
        append,
        remove,
    } = useFieldArray({
        name: 'entries',
        control,
    });

    const { mutate: submit, isPending } = useMutation<
        unknown,
        GivebutterAxiosError,
        TestMessageFormValues
    >({
        mutationFn: values =>
            sendTestMessage(accountId, messageId || 0, {
                addresses: values.entries.map(e => e.value),
            }),
        onSuccess: () => {
            success('Test message successfully sent!');
            forceRender();
        },
        onError: e => {
            error(e.response?.data.message || 'Unable to send test message. Please try again.');
        },
    });

    return (
        <Dialog.Root key={key}>
            <Dialog.Trigger asChild>{children}</Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content className="m-0 p-0" onClick={e => e.stopPropagation()}>
                    <Form.Provider {...methods}>
                        <form
                            onSubmit={e => {
                                e.stopPropagation();
                                handleSubmit(values => submit(values))(e);
                            }}
                        >
                            <Dialog.Header className="border-b border-gray-100 px-4 py-3">
                                <Dialog.Title className="text-lg font-medium sm:text-lg">
                                    Where should we send your test{' '}
                                    {channel === 'email' ? 'email' : 'message'}?
                                </Dialog.Title>
                            </Dialog.Header>
                            <Dialog.Body className="flex flex-col gap-4 p-4">
                                {channel === 'email' ? (
                                    <div className="flex flex-col gap-4">
                                        <p>
                                            You can send a test version of this message to others by
                                            entering their email address below.
                                        </p>

                                        <p>
                                            If you are using variables, the test email will contain
                                            generic values
                                        </p>
                                    </div>
                                ) : null}

                                {channel === 'sms' ? (
                                    <div className="flex flex-col gap-4">
                                        <p>
                                            You can send a test version of this message to others by
                                            entering their phone number below.
                                        </p>
                                        <p>
                                            If you are using variables, such as{' '}
                                            <code className="rounded-full bg-yellow-500 px-1.5 py-1 text-sm font-medium text-gray-900">{`{{contact.first_name}}`}</code>
                                            {/* */}, the SMS will contain generic values.
                                        </p>
                                    </div>
                                ) : null}

                                <Form.Container>
                                    <Form.Label name={path('entries')} className="sr-only">
                                        {channel} entries
                                    </Form.Label>
                                    <div className="flex w-full flex-col gap-2">
                                        {entries.map((entry, index) => (
                                            <div
                                                className="grid grid-cols-[1fr_40px] items-start"
                                                key={entry.id}
                                            >
                                                <div>
                                                    <Form.TextInputRoot
                                                        name={path(`entries.${index}.value`)}
                                                        rules={{ required: 'Field is required' }}
                                                    >
                                                        <Form.TextInputForm
                                                            placeholder={
                                                                channel === 'email'
                                                                    ? 'sam@example.com'
                                                                    : '(303) 123-4567'
                                                            }
                                                        />
                                                    </Form.TextInputRoot>
                                                    <Form.Error
                                                        name={path(`entries.${index}.value`)}
                                                    />
                                                </div>
                                                {index > 0 ? (
                                                    <Button
                                                        variant="transparent"
                                                        shape={'square'}
                                                        onClick={() => remove(index)}
                                                        aria-label="Remove entry"
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faTrash}
                                                            className="text-red-600"
                                                        />
                                                    </Button>
                                                ) : null}
                                            </div>
                                        ))}
                                    </div>
                                    {entries.length < 5 ? (
                                        <div className="flex justify-start">
                                            <Button
                                                variant="transparent"
                                                size="sm"
                                                onClick={() => append({ value: '' })}
                                            >
                                                Add {channel === 'email' ? 'email' : 'number'}
                                                <FontAwesomeIcon icon={faPlus} />
                                            </Button>
                                        </div>
                                    ) : null}
                                </Form.Container>
                            </Dialog.Body>
                            <div className="flex items-center justify-end gap-2 p-4">
                                <Dialog.Cancel asChild onClick={e => e.stopPropagation()}>
                                    <Button variant="outline" color="neutral">
                                        Cancel
                                    </Button>
                                </Dialog.Cancel>
                                <Form.SubmitButton
                                    onClick={e => e.stopPropagation()}
                                    isSubmitting={isPending}
                                >
                                    Send test {channel === 'sms' ? 'SMS' : channel}
                                </Form.SubmitButton>
                            </div>
                        </form>
                    </Form.Provider>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export { SendTestMessageDialog };
